import { useQuery } from "@tanstack/react-query";
import { API_URL } from "src/Utils/API";
import {
  AnalyticsOverview,
  BookingsTrendPoint,
  PopularTimePoint,
  PartySizeEntry,
  BookingSourceEntry,
  DayOfWeekEntry,
  DateRangeParams,
  OfferPerformanceData,
} from "../types/analytics";

// Analytics is served under the restaurant-scoped web route
// `/web/restaurants/{restaurantId}/analytics/*` (formerly `/admin/analytics/*`),
// with every endpoint wrapped in the ApiResponseV2 envelope.
const analyticsKeys = {
  overview: (p: DateRangeParams) => ["analytics", "overview", p] as const,
  trend: (p: DateRangeParams) => ["analytics", "trend", p] as const,
  popularTimes: (p: DateRangeParams) => ["analytics", "popular-times", p] as const,
  partySizeDistribution: (p: DateRangeParams) => ["analytics", "party-size", p] as const,
  bookingSources: (p: DateRangeParams) => ["analytics", "sources", p] as const,
  dayOfWeek: (p: DateRangeParams) => ["analytics", "day-of-week", p] as const,
  offerPerformance: (p: DateRangeParams) => ["analytics", "offer-performance", p] as const,
};

const buildUrl = (path: string, params: DateRangeParams): string => {
  const url = new URL(
    `${API_URL}/restaurants/${params.restaurantId}/analytics${path}`,
  );
  url.searchParams.set("startDate", params.startDate);
  url.searchParams.set("endDate", params.endDate);
  return url.toString();
};

// Every analytics endpoint returns the ApiResponseV2 envelope; unwrap `data`.
const fetchAnalytics = async <T>(url: string, errorLabel: string): Promise<T> => {
  const res = await fetch(url, { credentials: "include" });
  const body = await res.json();
  if (!body.success) throw new Error(errorLabel);
  return body.data as T;
};

const enabled = (p: DateRangeParams) => !!p.startDate && !!p.endDate && !!p.restaurantId;

// GET /web/restaurants/{restaurantId}/analytics/overview
// Returns high-level KPIs (totals + trend vs previous period) for the selected date range
export const useAnalyticsOverview = (params: DateRangeParams) =>
  useQuery({
    queryKey: analyticsKeys.overview(params),
    queryFn: () =>
      fetchAnalytics<AnalyticsOverview>(
        buildUrl("/overview", params),
        "Failed to fetch analytics overview",
      ),
    enabled: enabled(params),
  });

// GET /web/restaurants/{restaurantId}/analytics/bookings-trend
// Returns daily bookings and guest counts for the area chart
export const useBookingsTrend = (params: DateRangeParams) =>
  useQuery({
    queryKey: analyticsKeys.trend(params),
    queryFn: () =>
      fetchAnalytics<BookingsTrendPoint[]>(
        buildUrl("/bookings-trend", params),
        "Failed to fetch bookings trend",
      ),
    enabled: enabled(params),
  });

// GET /web/restaurants/{restaurantId}/analytics/popular-times
// Returns booking counts grouped by hour of day
export const usePopularTimes = (params: DateRangeParams) =>
  useQuery({
    queryKey: analyticsKeys.popularTimes(params),
    queryFn: () =>
      fetchAnalytics<PopularTimePoint[]>(
        buildUrl("/popular-times", params),
        "Failed to fetch popular times",
      ),
    enabled: enabled(params),
  });

// GET /web/restaurants/{restaurantId}/analytics/party-size-distribution
// Returns booking counts and percentages grouped by party size bucket
export const usePartySizeDistribution = (params: DateRangeParams) =>
  useQuery({
    queryKey: analyticsKeys.partySizeDistribution(params),
    queryFn: () =>
      fetchAnalytics<PartySizeEntry[]>(
        buildUrl("/party-size-distribution", params),
        "Failed to fetch party size distribution",
      ),
    enabled: enabled(params),
  });

// GET /web/restaurants/{restaurantId}/analytics/booking-sources
// Returns booking counts grouped by acquisition channel (direct, google, instagram, other)
export const useBookingSources = (params: DateRangeParams) =>
  useQuery({
    queryKey: analyticsKeys.bookingSources(params),
    queryFn: () =>
      fetchAnalytics<BookingSourceEntry[]>(
        buildUrl("/booking-sources", params),
        "Failed to fetch booking sources",
      ),
    enabled: enabled(params),
  });

// GET /web/restaurants/{restaurantId}/analytics/day-of-week
// Returns bookings and capacity utilization percentage grouped by day of week
export const useDayOfWeekPerformance = (params: DateRangeParams) =>
  useQuery({
    queryKey: analyticsKeys.dayOfWeek(params),
    queryFn: () =>
      fetchAnalytics<DayOfWeekEntry[]>(
        buildUrl("/day-of-week", params),
        "Failed to fetch day-of-week performance",
      ),
    enabled: enabled(params),
  });

// GET /web/restaurants/{restaurantId}/analytics/offer-performance
// Returns per-offer performance for deals and events over the selected period vs.
// the equal-length period before it (derived server-side).
export const useOfferPerformance = (params: DateRangeParams) =>
  useQuery({
    queryKey: analyticsKeys.offerPerformance(params),
    queryFn: () =>
      fetchAnalytics<OfferPerformanceData>(
        buildUrl("/offer-performance", params),
        "Failed to fetch offer performance",
      ),
    enabled: enabled(params),
  });
